import { AssessmentSubjectBase } from './AssessmentSubject';
import { CompletedAssessment, OperationalControl, UncompletedAssessment } from './OperationalControls';
import { OptionType } from './Types/GlobalType';

export interface DueDiligenceDashboardCompletedRiskAssessment {
    third_party_id: string;
    third_party_name: string;
    service_id: string;
    service_name: string;
    residual_risk_score: number;
    inherent_risk_score: number;
    control_effectiveness: number;
    completed_time: string;
    completed_on_time: boolean;
}

export interface DueDiligenceDashboardMonthlyRiskPortfolioSummary {
    month: MonthTimeframe;
    statistics: {
        average_inherent_risk: number;
        average_control_effectiveness: number;
        average_residual_risk: number;
    };
}

/** A TPRM service or UCRM use case, as displayed in the TPRM and UCRM tabs of the Dashboards page. */
export interface DueDiligenceDashboardAssessmentSubject extends AssessmentSubjectBase {
    is_overdue: boolean;
    is_upcoming: boolean;
}

export enum DueDiligenceDashboardRiskType {
    INHERENT = 'Inherent Risk',
    CONTROL_EFFECTIVENESS = 'Control Effectiveness',
    RESIDUAL = 'Residual Risk',
}

export const isValidDashboardRiskType = (value: unknown): value is DueDiligenceDashboardRiskType => Object.values(DueDiligenceDashboardRiskType).includes(value as DueDiligenceDashboardRiskType);

export const DashboardRiskOptions: OptionType[] = [
    {
        value: DueDiligenceDashboardRiskType.INHERENT,
        label: DueDiligenceDashboardRiskType.INHERENT,
    },
    {
        value: DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS,
        label: DueDiligenceDashboardRiskType.CONTROL_EFFECTIVENESS,
    },
    {
        value: DueDiligenceDashboardRiskType.RESIDUAL,
        label: DueDiligenceDashboardRiskType.RESIDUAL,
    },
];

export enum DateOptionSelectOption {
    LAST_YEAR = 'Last Year',
    LAST_SIX_MONTHS = 'Last 6 Months',
    LAST_THREE_MONTHS = 'Last 3 Months',
    NEXT_ONE_MONTH = 'Next 1 Month',
    NEXT_THREE_MONTHS = 'Next 3 Months',
    NEXT_SIX_MONTHS = 'Next 6 Months',
    NEXT_YEAR = 'Next Year',
    CUSTOM = 'custom',
}

export const isValidDateOption = (value: unknown): value is DateOptionSelectOption => Object.values(DateOptionSelectOption).includes(value as DateOptionSelectOption);

export const MyAssignmentsDateRangeOptions: DateOptionSelectOption[] = [DateOptionSelectOption.NEXT_ONE_MONTH, DateOptionSelectOption.NEXT_THREE_MONTHS, DateOptionSelectOption.NEXT_SIX_MONTHS, DateOptionSelectOption.NEXT_YEAR];
export const DefaultPastDateRangeOptions: DateOptionSelectOption[] = [DateOptionSelectOption.LAST_THREE_MONTHS, DateOptionSelectOption.LAST_SIX_MONTHS, DateOptionSelectOption.LAST_YEAR];
export const DefaultUpcomingDateRangeOptions: DateOptionSelectOption[] = [DateOptionSelectOption.NEXT_THREE_MONTHS, DateOptionSelectOption.NEXT_SIX_MONTHS, DateOptionSelectOption.NEXT_YEAR];
// OC Dashboards

export interface ControlStrengthResponse {
    controls_effectiveness_average: number;
    controls_by_effectiveness: {
        UNRATED: OperationalControl[];
        FAIL: OperationalControl[];
        WEAK: OperationalControl[];
        MODERATE: OperationalControl[];
        STRONG: OperationalControl[];
        ROBUST: OperationalControl[];
    };
}

export interface ComplianceSummary {
    regulation: string;
    met_count: number;
    not_met_count: number;
    not_mapped_count: number;
    awaiting_assessments_count: number;
}

export type MonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface MonthTimeframe {
    month: MonthNumber;
    year: number;
}

export interface ControlsSnapshot {
    month: MonthTimeframe;
    assessments: CompletedAssessment[];
    average_effectiveness: number;
}

export interface MonthlyEnabledControlsSnapshot {
    month: MonthTimeframe;
    timestamp: string;
    controls: OperationalControl[];
    average_control_effectiveness: number;
}

export interface ComplianceSummarySnapshot {
    month: MonthTimeframe;
    timestamp: string;
    summary: {
        regulation: string;
        met_count: number;
        not_met_count: number;
        not_mapped_count: number;
        awaiting_assessments_count: number;
    };
}

export interface ComplianceDetailsSnapshot {
    month: MonthTimeframe;
    timestamp: string;
    details: ComplianceDetails;
}

export interface ComplianceDetails {
    regulation: string;
    met: string[];
    not_met: string[];
    not_mapped: string[];
    awaiting_assessments: string[];
}

export interface SetTargetMaturityFFIECCATRequest {
    target_maturity: string;
}

export interface DomainStatusFFIECCAT {
    name: string;
    met_requirements_count: number;
    total_requirements_count: number;
}

export interface ComplianceHistoryFFIECCAT {
    periodic_summaries: ComplianceSummarySnapshot[];
    current_domain_statuses: DomainStatusFFIECCAT[];
    current_met_requirements_count: number;
    current_total_requirements_count: number;
}

export interface UpcomingAssessmentsForMonth {
    month: MonthTimeframe;
    assessments: UncompletedAssessment[];
}

export interface AssessmentsResponse {
    overdue: {
        marginally: UncompletedAssessment[];
        exceedingly: UncompletedAssessment[];
    };
    due_soon: UncompletedAssessment[];
    completed_recently: CompletedAssessment[];
    upcoming_by_month: UpcomingAssessmentsForMonth[];
}

export enum DashboardType {
    CONTROL_AUTOMATION = 'Control Automation',
    RISK_MANAGEMENT = 'Risk Management',
    THIRD_PARTY_RISK_MANAGEMENT = 'Third-Party Risk Management',
    UCRM = 'AI Governance',
}

/** The tabs of the Dashboards page that display issues and exceptions. */
export type IssuesExceptionsDashboardType = DashboardType.CONTROL_AUTOMATION | DashboardType.THIRD_PARTY_RISK_MANAGEMENT | DashboardType.UCRM;
